import {useRef} from 'react';

import {isNil} from 'ramda';

import {useOnMount} from 'shared/web';

import type {RowData} from '../types/RowData';
import {hasTextSelectionWithin} from '../utils/hasTextSelectionWithin';

/** Row lookup attribute set by GridRow. */
const ROW_SELECTOR = 'tr[data-row-id]';
const ROW_ID_ATTRIBUTE = 'data-row-id';
const INTERACTIVE_ELEMENT_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="checkbox"], [role="link"], [role="menuitem"]';
/** Press and release must stay within this many px to count as a click rather than a drag. */
const MAX_DRAG_DISTANCE_PX = 6;
const PRIMARY_BUTTON = 0;

type PressRecord = {
  rowElement: Element;
  rowId: string;
  pointerId: number;
  x: number;
  y: number;
};

type UseRowClickFallbackParams<T extends RowData> = {
  rows: T[];
  onRowClick: (row: T, event: React.MouseEvent | React.PointerEvent) => void;
};

/**
 * Restores row clicks the browser drops when the grid relayouts mid-press.
 *
 * A `click` is dispatched on the nearest common ancestor of the mousedown and mouseup
 * targets. When the rows move between those two events the pointer comes up over a
 * different element, so the click lands on the `<table>` and the row's own `onClick`
 * never runs — the user's click silently does nothing (T20-139432). Two relayouts do
 * this routinely: the active-filter bar appearing when a quick search is applied
 * (~45px, about one row height), and skeleton rows swapping for real ones when a
 * refetch settles.
 *
 * Purely additive: when the click does reach a row, GridRow's own handler runs and
 * this contributes nothing. It fires only for a press that started on a row, barely
 * moved, and whose click reached no row at all.
 *
 * The pressed row is identified by id, never by index: a refetch can seat a different
 * row at the same index mid-press, and navigating to whatever now sits there would be
 * worse than the dropped click. A row that has since disappeared is simply skipped.
 */
export const useRowClickFallback = <T extends RowData>(params: UseRowClickFallbackParams<T>) => {
  const pressRef = useRef<PressRecord | null>(null);
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelFallback = () => {
    if (isNil(fallbackTimeoutRef.current)) {
      return;
    }
    clearTimeout(fallbackTimeoutRef.current);
    fallbackTimeoutRef.current = null;
  };

  useOnMount(undefined, cancelFallback);

  const handlePointerDownCapture = (event: React.PointerEvent) => {
    cancelFallback();
    pressRef.current = null;

    if (event.button !== PRIMARY_BUTTON || !(event.target instanceof Element)) {
      return;
    }

    // Interactive cell content intentionally owns the press. If a relayout moves it
    // before release, recovering that press as a row click would run two unrelated
    // actions (or navigate when the intended control never completed its click).
    if (event.target.closest(INTERACTIVE_ELEMENT_SELECTOR)) {
      return;
    }

    const rowElement = event.target.closest(ROW_SELECTOR);
    const rowId = rowElement?.getAttribute(ROW_ID_ATTRIBUTE);
    if (isNil(rowElement) || isNil(rowId)) {
      return;
    }

    // Keep pointer ownership on the stable table-area element. Search can insert
    // controls above it and move the pressed row completely out from under the
    // pointer; without capture, pointerup then bubbles through the header instead
    // and this hook never gets a chance to recover the row action.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pressRef.current = {
      rowElement,
      rowId,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  };

  const recoverRowClick = (event: React.MouseEvent | React.PointerEvent) => {
    const press = pressRef.current;
    pressRef.current = null;

    if (isNil(press)) {
      return;
    }
    // A drag — text selection or a range selection — is not a click.
    if (
      Math.abs(event.clientX - press.x) > MAX_DRAG_DISTANCE_PX ||
      Math.abs(event.clientY - press.y) > MAX_DRAG_DISTANCE_PX
    ) {
      return;
    }
    if (hasTextSelectionWithin(press.rowElement)) {
      return;
    }

    const row = params.rows.find((candidate) => candidate.id === press.rowId);
    if (isNil(row)) {
      return;
    }

    params.onRowClick(row, event);
  };

  const handlePointerUpCapture = (event: React.PointerEvent) => {
    const press = pressRef.current;
    if (isNil(press)) {
      return;
    }

    if (event.currentTarget.hasPointerCapture?.(press.pointerId)) {
      event.currentTarget.releasePointerCapture?.(press.pointerId);
    }

    // Browsers normally dispatch click immediately after pointerup. Give that click
    // one turn to arrive and cancel this fallback. If a refetch replaced the pressed
    // row, Chromium may emit no click at all; recover the action after the turn.
    fallbackTimeoutRef.current = setTimeout(() => {
      fallbackTimeoutRef.current = null;
      recoverRowClick(event);
    });
  };

  const handleClickCapture = (event: React.MouseEvent) => {
    cancelFallback();

    if (!(event.target instanceof Element)) {
      pressRef.current = null;
      return;
    }
    // The click reached a row, so GridRow will handle it (or interactive cell content
    // will deliberately swallow it). A click retargeted to the table needs recovery.
    if (event.target.closest(ROW_SELECTOR)) {
      pressRef.current = null;
      return;
    }

    recoverRowClick(event);
  };

  const handlePointerCancelCapture = () => {
    cancelFallback();
    pressRef.current = null;
  };

  return {
    handlePointerDownCapture,
    handlePointerUpCapture,
    handlePointerCancelCapture,
    handleClickCapture,
  };
};
